import { Box, Edges } from '@react-three/drei'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { AssetVizMode } from 'features/assetViz/index'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { Vec3 } from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import { Color } from 'three'
import { GeckoFeature, GeckoFixedSensor, GeckoNozzle } from 'types'

/**
 * A box within a feature's procedural geometry
 * @param feature       The feature this box belong to
 * @param position      The position of the center of the base of this box
 * @param width         The width of the box
 * @param height        The height of the box
 * @param depth         The depth of the box
 * @param color         An optional override color for this box
 * @param debugColor    A color used to identify this box when debugging
 *                      features' procedural geometry
 * @constructor
 */
export const FeatureBox3D: React.FC<{
  feature: GeckoFeature
  position: Vec3
  width: number
  depth: number
  height: number
  color?: string
  debugColor: string
}> = ({ feature, position, width, height, depth, color, debugColor }) => {
  const { componentModel } = useSurfaceProps()
  const {
    mode,
    showFeatures,
    selection,
    draftSelection,
    assetColorString,
    weldColorString,
    showFeatureDebugColors,
    clipConfig,
    assetOpacity,
  } = useAssetVizInternal()

  const showThisFeature = React.useMemo(
    () =>
      typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature, componentModel }),
    [feature, componentModel, showFeatures]
  )

  const layers = React.useMemo(
    () =>
      !showThisFeature
        ? RenderLayerID.InvisibleNonCollidable
        : feature instanceof GeckoNozzle ||
            feature instanceof GeckoFixedSensor ||
            mode === AssetVizMode.AllFeaturesSelectable
          ? RenderLayerID.VisibleCollidable
          : RenderLayerID.VisibleNonCollidable,
    [feature, showThisFeature, mode]
  )

  const { meshColor, lineWidth, lineColor } = React.useMemo(
    () => ({
      meshColor: new Color(
        showFeatureDebugColors ? debugColor : (color ?? assetColorString)
      ).convertSRGBToLinear(),
      lineWidth: selection === feature || draftSelection === feature ? 4 : 2,
      lineColor: new Color(
        selection === feature || draftSelection === feature
          ? 'white'
          : weldColorString
      ).convertSRGBToLinear(),
    }),
    [
      assetColorString,
      color,
      debugColor,
      draftSelection,
      feature,
      selection,
      showFeatureDebugColors,
      weldColorString,
    ]
  )
  const featureOpacity = React.useMemo(
    () =>
      typeof assetOpacity === 'number'
        ? assetOpacity
        : assetOpacity(componentModel),
    [assetOpacity, componentModel]
  )

  return (
    <Box
      layers={layers}
      userData={{ extrudingFeature: feature }}
      position={[position[0], position[1] + depth / 2, position[2]]}
      scale={[width, depth, height]}>
      <meshBasicMaterial
        attach='material'
        color={meshColor}
        clippingPlanes={clipConfig.planes}
        opacity={featureOpacity}
      />
      <Edges
        layers={layers}
        linewidth={lineWidth}
        color={lineColor}
        clippingPlanes={clipConfig.planes}
      />
    </Box>
  )
}
